import React from "react";
import { motion } from "framer-motion";
import {
  Layers,
  Brain,
  Zap,
  Shield,
  Globe,
  Sparkles,
  ArrowRight,
  XCircle,
  CheckCircle2,
  DollarSign,
  Users2,
  Fingerprint,
} from "lucide-react";
import { Link } from "react-router-dom";

import {
  TechLabel,
  StatusBadge,
  CornerMarkers,
  PageContainer,
  motionFadeUp,
  C,
} from "../../design-system";
import { createPageUrl } from "../../utils";

const reasons = [
  { icon: Layers,      title: "Truly unified",      desc: "One ecosystem, one record across ATS, CRM, HRMS and Job Portal.",      accent: C.mint  },
  { icon: Brain,       title: "AI-first core",      desc: "Predictive analytics at every stage not bolted on, baked in.",       accent: C.coral },
  { icon: Zap,         title: "Workflow automation", desc: "Hiring, onboarding and HR tasks automated end-to-end.",                accent: C.gold  },
  { icon: DollarSign,  title: "Affordable scale",   desc: "Enterprise features at SMB-friendly pricing.",                          accent: C.mint  },
  { icon: Globe,       title: "Global by design",   desc: "Multi-country compliance and localized workflows from day one.",       accent: C.coral },
  { icon: Shield,      title: "Enterprise security", desc: "SOC 2, GDPR-ready, bank-grade encryption.",                            accent: C.gold  },
  { icon: Fingerprint, title: "White-label ready",   desc: "Custom branding for a seamless client experience.",                    accent: C.mint  },
  { icon: Users2,      title: "Scales with you",    desc: "From 5-person startups to 50,000-person enterprises.",                  accent: C.coral },
];

const compareRows = [
  { feature: "Unified ATS + CRM + HRMS",            them: false, us: true  },
  { feature: "AI fit scoring on candidate side",    them: false, us: true  },
  { feature: "Predictive hiring analytics",         them: "limited", us: true  },
  { feature: "Multi-country compliance",            them: "add-on", us: true  },
  { feature: "Workflow automation included",        them: "add-on", us: true  },
  { feature: "White-labeling",                       them: false,  us: true  },
  { feature: "Single bill across modules",          them: false,  us: true  },
];

export default function WhyWebIdeal() {
  return (
    <div
      className="relative"
      style={{
        background: C.paper,
        color: C.forest,
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* HERO */}
      <section
        className="relative pt-16 pb-16 md:pt-24 md:pb-20 border-b ds-bg-spotlight"
        style={{ borderColor: C.hairline }}
      >
        <PageContainer>
          <motion.div initial="hidden" animate="show" variants={motionFadeUp}>
            <motion.div variants={motionFadeUp} custom={0}>
              <StatusBadge>Why WebIdeal · 08 reasons</StatusBadge>
            </motion.div>
            <motion.h1
              variants={motionFadeUp}
              custom={1}
              className="mt-5 text-5xl sm:text-6xl lg:text-[5rem] xl:text-[5.75rem] font-extrabold leading-[0.92] tracking-tight max-w-4xl"
              style={{ color: C.forest }}
            >
              Built different.
              <br />
              <span style={{ color: `${C.forest}80` }}>Built better.</span>
            </motion.h1>
            <motion.p
              variants={motionFadeUp}
              custom={2}
              className="mt-6 text-lg md:text-xl leading-relaxed max-w-2xl"
              style={{ color: C.grid }}
            >
              Eight reasons modern teams switch to WebIdeal and why most don't
              switch back.
            </motion.p>
          </motion.div>
        </PageContainer>
      </section>

      {/* REASONS GRID */}
      <section className="py-20 md:py-24 border-b" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px"
            style={{ background: C.hairline }}
          >
            {reasons.map((r, i) => (
              <motion.div
                key={r.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="ds-spatial relative p-7 md:p-8 transition-colors duration-200 hover:bg-white"
                style={{ background: C.paper, borderLeft: `4px solid ${r.accent}` }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div
                    className="w-11 h-11 flex items-center justify-center"
                    style={{ background: C.forest, color: C.paper }}
                  >
                    <r.icon className="w-5 h-5" strokeWidth={1.6} />
                  </div>
                </div>
                <h3 className="text-lg font-bold leading-tight" style={{ color: C.forest }}>
                  {r.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: C.grid }}>
                  {r.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </PageContainer>
      </section>

      {/* COMPARE */}
      <section className="py-20 md:py-24 border-b" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <div className="mb-12 max-w-2xl">
            <StatusBadge>Side-by-side</StatusBadge>
            <h2
              className="mt-4 text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight"
              style={{ color: C.forest }}
            >
              How we compare to legacy stacks.
            </h2>
          </div>

          <div className="border" style={{ borderColor: C.hairline, background: C.paper }}>
            {/* Header */}
            <div className="grid grid-cols-3 gap-px border-b" style={{ borderColor: C.hairline, background: C.hairline }}>
              <div className="p-4" style={{ background: C.paper }}>
                <TechLabel style={{ color: `${C.grid}99` }}>capability</TechLabel>
              </div>
              <div className="p-4" style={{ background: C.paper }}>
                <TechLabel style={{ color: C.coral }}>Legacy stack</TechLabel>
              </div>
              <div className="p-4" style={{ background: C.paper }}>
                <TechLabel style={{ color: C.forest }}>WebIdeal</TechLabel>
              </div>
            </div>

            {/* Rows */}
            {compareRows.map((row, idx) => (
              <div
                key={row.feature}
                className="grid grid-cols-3 gap-px"
                style={{
                  background: C.hairline,
                  borderBottom: idx < compareRows.length - 1 ? `1px solid ${C.hairline}` : "none",
                }}
              >
                <div className="p-4 text-sm" style={{ background: C.paper, color: C.grid }}>
                  {row.feature}
                </div>
                <div className="p-4 text-sm" style={{ background: C.paper }}>
                  <CompareCell value={row.them} negative />
                </div>
                <div className="p-4 text-sm" style={{ background: C.paper }}>
                  <CompareCell value={row.us} />
                </div>
              </div>
            ))}
          </div>
        </PageContainer>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24" style={{ background: C.forest, color: C.paper }}>
        <PageContainer>
          <div
            className="relative border p-10 md:p-14 text-center"
            style={{ borderColor: "rgba(158,255,191,0.25)" }}
          >
            <CornerMarkers color={C.mint} />
            <div className="flex justify-center mb-5">
              <StatusBadge color={C.mint}>Try it</StatusBadge>
            </div>
            <h2 className="text-3xl md:text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight">
              See it on your own data.
            </h2>
            <p className="mt-4 text-base md:text-lg max-w-2xl mx-auto" style={{ color: "rgba(247,247,245,0.75)" }}>
              Bring a real role, see real candidates ranked. We'll set up a sandbox in 24 hours.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to={createPageUrl("Contact")}>
                <button
                  type="button"
                  className="ds-skeu-btn-mint cursor-pointer h-12 px-7 text-sm font-semibold inline-flex items-center"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Book a sandbox
                </button>
              </Link>
              <Link to={createPageUrl("Pricing")}>
                <button
                  type="button"
                  className="cursor-pointer h-12 px-6 text-sm font-semibold border bg-transparent inline-flex items-center hover:bg-[#9EFFBF] hover:text-[#1A3C2B] transition-colors duration-150"
                  style={{ borderColor: "rgba(158,255,191,0.4)", color: C.paper }}
                >
                  See pricing
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </Link>
            </div>
          </div>
        </PageContainer>
      </section>
    </div>
  );
}

function CompareCell({ value, negative }) {
  if (value === true) {
    return (
      <span className="inline-flex items-center gap-2" style={{ color: C.forest }}>
        <CheckCircle2 className="w-4 h-4" style={{ color: negative ? C.coral : C.forest }} strokeWidth={2.2} />
        <span className="font-semibold">Yes</span>
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex items-center gap-2" style={{ color: `${C.grid}88` }}>
        <XCircle className="w-4 h-4" style={{ color: C.coral }} strokeWidth={2.2} />
        <span>No</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2" style={{ color: C.grid }}>
      <span
        className="w-2 h-2"
        style={{ background: negative ? C.coral : C.forest }}
        aria-hidden="true"
      />
      <span className="font-medium capitalize">{value}</span>
    </span>
  );
}
